import { resolveProperty } from '@ayumia/component-runtime';
import { isDecimalText, parseDecimalInput, sumDecimalText } from './decimal-text';
import type { ViewCommand } from './ViewContext';

/**
 * État de l'éditeur de lignes (ADR-136 §2, core-component-inputs 1.6.0 `line_editor`) : le pendant
 * en saisie de l'onglet `lines`, sans React et sans aucun nom d'objet métier. Ce module ne fait que
 * de la présentation : tout ce qu'il grise ou refuse, le serveur le revalide.
 *
 * - Une quantité reste du texte décimal exact (R2) jusqu'à l'envoi ; elle ne devient le nombre du fil
 *   que si elle tient en `max_significant_digits` chiffres significatifs et se relit à l'identique.
 * - Une ligne vide n'est jamais un zéro : un zéro se saisit (« absent »).
 * - Une référence saisie se range en identifiant seul quand le chemin finit par `_id` (`lot_id`),
 *   sinon en référence `{ type, id }` (`item`) : la convention des contrats du dépôt.
 * - Un instant se saisit au fuseau que la feuille rend (site, sinon tenant), jamais à celui du
 *   navigateur (R1, ADR-085).
 */

export type LineEditorSpec = NonNullable<ViewCommand['line_editor']>;
export type LineEditorColumn = LineEditorSpec['columns'][number];
export type Row = Record<string, unknown>;

/** Une ligne de l'éditeur : les données (pré-remplies ou choisies) et les saisies décimales en texte. */
export interface EditorLine {
  /** Identifiant stable pour React et le brouillon. */
  id: string;
  data: Row;
  /** Chemin d'une colonne de saisie décimale → texte tapé (jamais un nombre). */
  entered: Record<string, string>;
  prefilled: boolean;
}

export function valueAt(row: unknown, path: string): unknown {
  let current: unknown = row;
  for (const segment of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Row)[segment];
  }
  return current;
}

export function setAt(row: Row, path: string, value: unknown): Row {
  const [head, ...rest] = path.split('.');
  const out: Row = { ...row };
  if (rest.length === 0) {
    if (value === undefined) delete out[head!];
    else out[head!] = value;
    return out;
  }
  const child = out[head!];
  out[head!] = setAt(child !== null && typeof child === 'object' && !Array.isArray(child) ? (child as Row) : {}, rest.join('.'), value);
  return out;
}

const inputOf = (column: LineEditorColumn): 'none' | 'decimal' | 'reference' | 'enum' => column.input ?? 'none';
export const decimalInputs = (spec: LineEditorSpec): LineEditorColumn[] => spec.columns.filter((column) => inputOf(column) === 'decimal');

/** Clé d'une ligne (chemins `key`) : une référence compte par son type et son identifiant. */
export function lineKey(spec: LineEditorSpec, data: Row): string {
  return JSON.stringify(
    spec.key.map((path) => {
      const value = valueAt(data, path);
      if (value !== null && typeof value === 'object' && typeof (value as Row)['id'] === 'string') return `${String((value as Row)['type'] ?? '')}:${String((value as Row)['id'])}`;
      return value ?? null;
    }),
  );
}

/** Évaluation d'une expression canonique (ADR-039) sur une portée, par l'évaluateur unique ; une erreur vaut `null`. */
export function evaluate(expression: unknown, scope: Readonly<Record<string, unknown>>): unknown {
  try {
    return resolveProperty({ expression }, scope) ?? null;
  } catch {
    return null;
  }
}

/** Condition sur `$payload` et `$context` ; absente : `fallback`. Une condition qui ne s'évalue pas vaut faux. */
export function condition(expression: unknown, scope: Readonly<Record<string, unknown>>, fallback: boolean): boolean {
  if (expression === undefined) return fallback;
  return evaluate({ $if: [expression, true, false] }, scope) === true;
}

/** Paramètres de la requête de pré-remplissage : un paramètre vide n'est pas envoyé. */
export function prefillParameters(spec: LineEditorSpec, scope: Readonly<Record<string, unknown>>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [name, expression] of Object.entries(spec.prefill?.parameters ?? {})) {
    const value = evaluate(expression, scope);
    if (typeof value === 'string' && value !== '') out[name] = value;
    else if (typeof value === 'number' || typeof value === 'boolean') out[name] = String(value);
  }
  return out;
}

/**
 * Paramètres qui identifient la feuille (le brouillon s'y rattache) : ceux qui lisent une référence
 * de la charge utile (le local, le comptage recompté), jamais un instant ni un choix comme la portée.
 * `fieldTypes` : type déclaré de chaque champ de la charge utile ; un champ non déclaré (pré-rempli par
 * l'action) compte comme une référence.
 */
export function identityParameters(spec: LineEditorSpec, parameters: Readonly<Record<string, string>>, fieldTypes: Readonly<Record<string, string>> = {}): Record<string, string> {
  const instants = new Set(spec.prefill?.instant_fields ?? []);
  const out: Record<string, string> = {};
  for (const [name, expression] of Object.entries(spec.prefill?.parameters ?? {})) {
    const reference = typeof expression === 'string' ? /^\$payload\.([a-z][a-z0-9_]*)$/.exec(expression) : null;
    if (reference === null) continue;
    const field = reference[1]!;
    if (instants.has(field)) continue;
    const type = fieldTypes[field];
    if (type !== undefined && type !== 'reference') continue;
    if (parameters[name] !== undefined) out[name] = parameters[name]!;
  }
  return out;
}

let sequence = 0;
export const newLineId = (): string => `l${(sequence += 1).toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** Lignes rendues par la requête (`lines_path`) passées par la correspondance `map`. */
export function prefilledRows(spec: LineEditorSpec, response: unknown): Row[] {
  const prefill = spec.prefill;
  if (prefill === undefined) return [];
  const rows = valueAt(response, prefill.lines_path);
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((row): row is Row => row !== null && typeof row === 'object')
    .map((row) => {
      let data: Row = {};
      for (const [target, source] of Object.entries(prefill.map)) {
        const value = valueAt(row, source);
        if (value !== undefined && value !== null) data = setAt(data, target, value);
      }
      return data;
    });
}

/**
 * Lignes de l'éditeur après une (nouvelle) lecture de la feuille : une saisie déjà faite est gardée
 * pour la même clé ; une ligne ajoutée à la main est gardée telle quelle.
 */
export function mergePrefill(spec: LineEditorSpec, rows: readonly Row[], previous: readonly EditorLine[]): EditorLine[] {
  const before = new Map(previous.filter((line) => line.prefilled).map((line) => [lineKey(spec, line.data), line]));
  const out: EditorLine[] = rows.map((data) => {
    const kept = before.get(lineKey(spec, data));
    return { id: kept?.id ?? newLineId(), data, entered: kept?.entered ?? {}, prefilled: true };
  });
  return [...out, ...previous.filter((line) => !line.prefilled)];
}

export type Entered = { kind: 'empty' } | { kind: 'ok'; decimal: string } | { kind: 'invalid'; message: string };

/** Chiffres significatifs d'un décimal texte (« 0.0012 » : 2 ; « 1234.567 » : 7 ; « 0 » : 1). */
export function significantDigits(decimal: string): number {
  const digits = decimal.replace(/^-/, '').replace('.', '').replace(/^0+/, '');
  const trimmed = decimal.includes('.') ? digits.replace(/0+$/, '') : digits;
  return Math.max(1, trimmed.length);
}

/** Forme canonique d'un décimal texte : sans zéro superflu (« 1.50 » → « 1.5 », « -0 » → « 0 »). */
export function canonicalDecimal(decimal: string): string {
  return sumDecimalText([decimal]);
}

/**
 * Nombre du fil pour un décimal texte, ou `null` s'il ne se relit pas à l'identique : la conversion
 * n'a lieu qu'à l'envoi, et seulement si le texte du nombre redonne exactement le décimal saisi.
 */
export function wireNumber(decimal: string): number | null {
  const value = Number(decimal);
  if (!Number.isFinite(value)) return null;
  const back = String(value);
  if (!isDecimalText(back)) return null;
  return canonicalDecimal(back) === canonicalDecimal(decimal) ? value : null;
}

const compareDecimal = (a: string, b: string): number => {
  const difference = sumDecimalText([a, b.startsWith('-') ? b.slice(1) : `-${b}`]);
  return difference === '0' ? 0 : difference.startsWith('-') ? -1 : 1;
};

/** Saisie d'une colonne décimale : vide, décimal exact, ou refus avec sa raison. */
export function parseEntered(column: LineEditorColumn, raw: string | undefined): Entered {
  if (raw === undefined || raw.trim() === '') return { kind: 'empty' };
  const decimal = parseDecimalInput(raw);
  if (decimal === null) return { kind: 'invalid', message: 'Nombre attendu (virgule ou point).' };
  const limit = column.max_significant_digits ?? 15;
  if (significantDigits(decimal) > limit) return { kind: 'invalid', message: `Au plus ${limit} chiffres significatifs.` };
  if (column.min !== undefined && compareDecimal(decimal, column.min) < 0) return { kind: 'invalid', message: `Au moins ${column.min.replace('.', ',')}.` };
  if (wireNumber(decimal) === null) return { kind: 'invalid', message: 'Ce nombre ne se transmet pas exactement.' };
  return { kind: 'ok', decimal: canonicalDecimal(decimal) };
}

/** Colonnes affichées : une colonne `optional` disparaît quand aucune ligne pré-remplie ne porte sa valeur (feuille aveugle). */
export function visibleColumns(spec: LineEditorSpec, lines: readonly EditorLine[]): LineEditorColumn[] {
  return spec.columns.filter((column) => column.optional !== true || lines.some((line) => line.prefilled && valueAt(line.data, column.path) !== undefined && valueAt(line.data, column.path) !== null));
}

/** Unité d'une ligne pour une colonne décimale (`unit_path`) : la sienne, sinon celle d'une ligne du même premier élément de clé. */
export function unitOf(spec: LineEditorSpec, lines: readonly EditorLine[], line: EditorLine, column: LineEditorColumn): string | undefined {
  if (column.unit_path === undefined) return undefined;
  const own = valueAt(line.data, column.unit_path);
  if (typeof own === 'string' && own !== '') return own;
  const anchor = JSON.stringify(valueAt(line.data, spec.key[0]!) ?? null);
  if (anchor === 'null') return undefined;
  for (const other of lines) {
    if (other === line || JSON.stringify(valueAt(other.data, spec.key[0]!) ?? null) !== anchor) continue;
    const unit = valueAt(other.data, column.unit_path);
    if (typeof unit === 'string' && unit !== '') return unit;
  }
  return undefined;
}

export interface EditorStatus {
  /** Raisons qui grisent l'envoi, dans l'ordre où les lire. */
  reasons: string[];
  /** Ligne → chemin → message. */
  invalid: Map<string, Record<string, string>>;
  duplicates: Set<string>;
  /** Lignes qui doivent recevoir une valeur et n'en ont pas. */
  missing: Set<string>;
  /** Lignes qui partiront. */
  payloadLines: Row[];
  /** Les lignes ajoutées sont-elles permises ? */
  canAdd: boolean;
  required: boolean;
}

export interface StatusInputs {
  lines: readonly EditorLine[];
  scope: Readonly<Record<string, unknown>>;
  /** Raison annoncée par la feuille (`blocking_path`). */
  blocking?: { code?: string; message: string } | null;
  /** La feuille est-elle en cours de lecture, ou refusée ? */
  prefill: 'none' | 'loading' | 'ready' | 'error';
}

/** Ce qui partira, et ce qui empêche l'envoi : refus visibles d'avance (ADR-136 §2). */
export function editorStatus(spec: LineEditorSpec, inputs: StatusInputs): EditorStatus {
  const { lines, scope } = inputs;
  const reasons: string[] = [];
  const invalid = new Map<string, Record<string, string>>();
  const duplicates = new Set<string>();
  const missing = new Set<string>();
  const payloadLines: Row[] = [];
  const required = condition(spec.prefilled_required_when, scope, false);
  const canAdd = condition(spec.add_when, scope, true);
  const decimals = decimalInputs(spec);
  const flag = (line: EditorLine, path: string, message: string): void => {
    invalid.set(line.id, { ...(invalid.get(line.id) ?? {}), [path]: message });
  };

  if (inputs.prefill === 'loading') reasons.push('Lecture des lignes en cours.');
  if (inputs.prefill === 'error') reasons.push('Les lignes à saisir n’ont pas pu être lues.');
  if (inputs.blocking !== undefined && inputs.blocking !== null) reasons.push(inputs.blocking.message);

  const seen = new Map<string, string>();
  for (const line of lines) {
    const parsed = decimals.map((column) => ({ column, entered: parseEntered(column, line.entered[column.path]) }));
    for (const { column, entered } of parsed) if (entered.kind === 'invalid') flag(line, column.path, entered.message);
    const empty = parsed.filter(({ entered }) => entered.kind === 'empty');
    const filled = empty.length === 0;
    // Une ligne pré-remplie obligatoire, ou une ligne ajoutée à la main, doit recevoir une valeur ; zéro se saisit.
    if (!filled && ((line.prefilled && required) || !line.prefilled)) missing.add(line.id);
    if (!line.prefilled) {
      for (const column of spec.columns) {
        if (inputOf(column) !== 'reference' || !spec.key.includes(column.path)) continue;
        if (valueAt(line.data, column.path) === undefined) flag(line, column.path, `${column.label['fr'] ?? column.path} à choisir.`);
      }
      for (const column of decimals) if (column.unit_path !== undefined && unitOf(spec, lines, line, column) === undefined) flag(line, column.unit_path, 'Unité à saisir.');
    }
    const key = lineKey(spec, line.data);
    const first = seen.get(key);
    if (first !== undefined) {
      duplicates.add(line.id);
      duplicates.add(first);
    } else seen.set(key, line.id);
    if (!filled || invalid.has(line.id)) continue;
    let out: Row = {};
    for (const path of spec.key) {
      const value = valueAt(line.data, path);
      if (value !== undefined && value !== null) out = setAt(out, path, value);
    }
    for (const column of spec.columns) {
      const input = inputOf(column);
      if (input === 'reference' || input === 'enum') {
        const value = valueAt(line.data, column.path);
        if (value !== undefined && value !== null) out = setAt(out, column.path, value);
      }
    }
    for (const { column, entered } of parsed) {
      if (entered.kind !== 'ok') continue;
      out = setAt(out, column.path, wireNumber(entered.decimal));
      const unit = unitOf(spec, lines, line, column);
      if (column.unit_path !== undefined && unit !== undefined) out = setAt(out, column.unit_path, unit);
    }
    payloadLines.push(out);
  }

  const missingPrefilled = lines.filter((line) => line.prefilled && missing.has(line.id)).length;
  const missingAdded = lines.filter((line) => !line.prefilled && missing.has(line.id)).length;
  if (missingPrefilled > 0) reasons.push(`${missingPrefilled} ligne(s) sans quantité : saisir 0 pour une position absente.`);
  if (missingAdded > 0) reasons.push(`${missingAdded} ligne(s) ajoutée(s) sans quantité.`);
  if (invalid.size > 0) reasons.push(`${invalid.size} ligne(s) à corriger.`);
  if (duplicates.size > 0) reasons.push(`${duplicates.size} lignes portent la même clé : chaque position ne se saisit qu’une fois.`);
  if (spec.max_lines !== undefined && payloadLines.length > spec.max_lines) {
    reasons.push(`${payloadLines.length} lignes : au plus ${spec.max_lines} par envoi.${spec.overflow_hint === undefined ? '' : ` ${spec.overflow_hint['fr'] ?? ''}`}`.trim());
  }
  if (payloadLines.length === 0 && lines.length > 0 && missingPrefilled === 0 && missingAdded === 0 && invalid.size === 0) reasons.push('Aucune ligne saisie.');
  return { reasons, invalid, duplicates, missing, payloadLines, canAdd, required };
}

/** Totaux exacts d'une colonne décimale, par unité (comme l'onglet `lines`), en texte. */
export function columnTotal(spec: LineEditorSpec, lines: readonly EditorLine[], column: LineEditorColumn): Array<{ unit: string; total: string }> {
  const byUnit = new Map<string, string[]>();
  for (const line of lines) {
    let value: string | null = null;
    if (inputOf(column) === 'decimal') {
      const entered = parseEntered(column, line.entered[column.path]);
      value = entered.kind === 'ok' ? entered.decimal : null;
    } else {
      const raw = valueAt(line.data, column.path);
      value = isDecimalText(raw) ? raw : null;
    }
    if (value === null) continue;
    const unit = unitOf(spec, lines, line, column) ?? '';
    byUnit.set(unit, [...(byUnit.get(unit) ?? []), value]);
  }
  return [...byUnit.entries()].map(([unit, values]) => ({ unit, total: sumDecimalText(values) }));
}

/** Lignes dont un chemin de recherche vaut exactement la saisie (« aller à la ligne », douchette en mode clavier). */
export function lookupLine(spec: LineEditorSpec, lines: readonly EditorLine[], code: string): EditorLine | undefined {
  const wanted = code.trim();
  if (wanted === '' || spec.lookup === undefined) return undefined;
  return lines.find((line) => spec.lookup!.paths.some((path) => {
    const value = valueAt(line.data, path);
    return (typeof value === 'string' || typeof value === 'number') && String(value) === wanted;
  }));
}

/** Valeur d'une référence choisie : identifiant seul pour un chemin en `_id`, sinon `{ type, id }`. */
export function referenceValue(column: LineEditorColumn, id: string): unknown {
  return /(^|\.)[a-z0-9_]*_id$/.test(column.path) ? id : { type: column.reference_type ?? '', id };
}

/* ---- Instants au fuseau du site (R1, ADR-085) ---- */

function zoneParts(instant: Date, timeZone: string): Record<string, number> {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }).formatToParts(instant);
  const out: Record<string, number> = {};
  for (const part of parts) if (part.type !== 'literal') out[part.type] = Number.parseInt(part.value, 10);
  return out;
}

/** Décalage du fuseau à un instant, en millisecondes (heure locale − UTC). */
function offsetAt(instant: Date, timeZone: string): number {
  const p = zoneParts(instant, timeZone);
  const local = Date.UTC(p['year']!, p['month']! - 1, p['day']!, p['hour']!, p['minute']!, p['second']!);
  return local - Math.floor(instant.getTime() / 1000) * 1000;
}

/** Instant ISO → « AAAA-MM-JJTHH:mm » lu au fuseau donné (champ `datetime-local`). */
export function instantToZonedInput(value: unknown, timeZone: string): string {
  if (typeof value !== 'string') return '';
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return '';
  const p = zoneParts(instant, timeZone);
  const pad = (n: number | undefined): string => String(n ?? 0).padStart(2, '0');
  return `${String(p['year']).padStart(4, '0')}-${pad(p['month'])}-${pad(p['day'])}T${pad(p['hour'])}:${pad(p['minute'])}`;
}

/** « AAAA-MM-JJTHH:mm » saisi au fuseau donné → instant ISO UTC ; `null` si la saisie n'est pas une heure. */
export function zonedInputToInstant(local: string, timeZone: string): string | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/.exec(local);
  if (match === null) return null;
  const [year, month, day, hour, minute] = match.slice(1).map((part) => Number.parseInt(part, 10)) as [number, number, number, number, number];
  const wall = Date.UTC(year, month - 1, day, hour, minute);
  // Deux passes : le décalage se lit à l'instant trouvé, ce qui tient compte du changement d'heure.
  let guess = wall - offsetAt(new Date(wall), timeZone);
  guess = wall - offsetAt(new Date(guess), timeZone);
  return new Date(guess).toISOString();
}
